import { withMetadata } from '@pactswap/cwap-cm-lib/history/offchain';
import { HISTORY_ACCESS_FEE } from '@pactswap/cwap-cm-lib/history/shared';
import { constructCall, constructUiCommand } from '@pactswap/cwap-cm-lib/offchain';
import { FEE, serializeOrdersBatch, PUBLIC_METHODS, ordersBatchToList, calculateOrdersListParams } from './shared';
/**
 * Internal helper function that constructs a contract call object for
 * creating orders and might be used to create a UI-command with QR code for wallet scanning.
 * Validates order amounts and calculates the total cost including fees.
 *
 * @param contractId - The L2 contract address where the order will be created
 * @param accessId - Optional unique identifier for history tracking
 * @param orders - Batch of orders to create (BTC or EVM format)
 * @returns A ContractCall object ready to be included in a UI command
 * @throws {Error} If l1Amount is less than minL1Amount
 *
 * @example
 * ```typescript
 * const call = constructCreateOrdersCall({
 *   contractId: '0x...',
 *   orders: [[1, { baseAmount: 1000n, l1Amount: 500n, minL1Amount: 450n, l1Address: '0x...' }]],
 *   accessId: 'tracking-id-123'
 * });
 * ```
 */
const constructCreateOrdersCall = ({ contractId, accessId, orders }) => {
    const { l1Amount, minL1Amount, definedBaseAmount, fee } = calculateOrdersListParams(ordersBatchToList(orders));
    if (l1Amount < minL1Amount) {
        throw new Error('creteNewOrderUiCommand: l1Amount is less than minimum value');
    }
    return constructCall(contractId, PUBLIC_METHODS.CREATE_ORDER, withMetadata([serializeOrdersBatch(orders)], accessId), definedBaseAmount + fee + (accessId ? HISTORY_ACCESS_FEE : 0n));
};
/**
 * Constructs a contract call object for creating one or more BTC-like orders (BTC, LTC, DOGE)
 * and might be used to create a UI-command with QR code for wallet scanning.
 *
 * @param data - Order creation data for BTC-like chains
 * @param data.contractId - The L2 contract address for the BTC-like token
 * @param data.orders - Batch of BTC orders with chainData (PSBT, vout)
 * A batch is an array of instructions for creating sets of orders. Each instruction is
 * an array with the following elements:
 * - The number of orders to create with the base template
 * - The base order template with `baseAmount`, `l1Amount`, `minL1Amount`, `l1Address`, `chainData`
 * - Optional array of order-specific overrides for each order in the batch
 *   - Each override is an object with the following properties:
 *     - `baseAmount`: The amount of base tokens (CWEB) to spend
 *     - `l1Amount`: The amount of L1 tokens (BTC/LTC/DOGE) to receive
 *     - `minL1Amount`: The minimum acceptable L1 token amount
 *     - `l1Address`: The recipient address on the L1 chain
 *     - `chainData`: The BTC-like chain-specific transaction data (PSBT, l1TxId, vout)
 * @param data.accessId - Optional unique identifier for history tracking
 * @returns A ContractCall object for creating BTC orders
 *
 * @example
 * ```typescript
 * type CreateOrderParams = {
 *   baseAmount?: bigint;
 *   l1Amount: bigint;
 *   l1Address: string;
 *   minL1Amount?: bigint;
 *   chainData?: ChainData;
 * };
 *
 * const qrCommand = constructCreateBtcOrdersCall({
 *   contractId: '0x...',
 *   orders: [[
 *     2, // Create 2 orders
 *     {
 *       baseAmount: 1000000000000000000n, // 1 CWEB
 *       l1Amount: 50000000n, // 0.5 BTC (in satoshis)
 *       minL1Amount: 450n,
 *       l1Address: 'bc1q...',
 *     } satisfies Partial<CreateOrderParams>,
 *     [
 *       {
 *         chainData: {
 *           psbt: 'base64-encoded-psbt',
 *           l1TxId: 'txid',
 *           vout: 0
 *         }
 *       } satisfies Partial<CreateOrderParams>,
 *       {
 *         chainData: {
 *           psbt: 'base64-encoded-psbt',
 *           l1TxId: 'txid',
 *           vout: 1
 *         }
 *       } satisfies Partial<CreateOrderParams>,
 *     ]
 *   ]],
 * });
 *
 * // Use in QRCode component
 * <QRCode value={qrCommand} />
 * ```
 */
export const constructCreateBtcOrdersCall = (data) => {
    return constructCreateOrdersCall(data);
};
/**
 * Constructs a contract call object for creating one or more EVM orders (ETH, USDC, etc.)
 * and might be used to create a UI-command with QR code for wallet scanning.
 *
 * @param data - Order creation data for EVM chains
 * @param data.contractId - The L2 contract address for the EVM token
 * @param data.orders - Batch of EVM orders (no chainData required)
 * A batch is an array of instructions for creating sets of orders. Each instruction is
 * an array with the following elements:
 * - The number of orders to create with the base template
 * - The base order template with `baseAmount`, `l1Amount`, `minL1Amount`, `l1Address`, `chainData`
 * - Optional array of order-specific overrides for each order in the batch
 *   - Each override is an object with the following properties:
 *     - `baseAmount`: The amount of base tokens (CWEB) to spend
 *     - `l1Amount`: The amount of L1 tokens (ETH/USDC/etc.) to receive
 *     - `minL1Amount`: The minimum acceptable L1 token amount
 *     - `l1Address`: The recipient address on the EVM chain
 * @param data.accessId - Optional unique identifier for history tracking
 * @returns A ContractCall object for creating EVM orders
 *
 * @example
 * ```typescript
 * type CreateOrderParams = {
 *   baseAmount?: bigint;
 *   l1Amount: bigint;
 *   l1Address: string;
 *   minL1Amount?: bigint;
 * };
 *
 * const uiCommand = constructCreateOrdersUiCommand({
 *   contractId: '0x...',
 *   orders: [[
 *    2, // Create 2 orders
 *    { baseAmount: 1000n, l1Amount: 500n, l1Address: '0x...' } satisfies Partial<CreateOrderParams>,
 *    [
 *      { minL1Amount: 150n } satisfies Partial<CreateOrderParams>,
 *      { minL1Amount: 100n } satisfies Partial<CreateOrderParams>,
 *    ]
 *   ]],
 *   accessId: 'tracking-id-123'
 * });
 *
 * // Use in QRCode component
 * <QRCode value={qrCommand} />
 * ```
 */
export const constructCreateEvmOrdersCall = (data) => {
    return constructCreateOrdersCall(data);
};
/**
 * Constructs a contract call object for canceling one or more orders
 * and might be used to create a UI-command with QR code for wallet scanning.
 *
 * @param contractId - The L2 contract address where orders exist
 * @param orderIds - Array of order IDs to cancel
 * @param accessId - Optional unique identifier for history tracking
 * @returns A ContractCall object for canceling orders
 *
 * @example
 * ```typescript
 * const call = constructCancelOrdersCall({
 *   contractId: '0x...',
 *   orderIds: ['order-id-1', 'order-id-2', 'order-id-3'],
 *   accessId: 'tracking-id-456'
 * });
 *
 * // Use in QRCode component
 * <QRCode value={qrCommand} />
 * ```
 */
export const constructCancelOrdersCall = ({ contractId, orderIds, accessId }) => {
    return constructCall(contractId, PUBLIC_METHODS.CANCEL_ORDER, withMetadata([orderIds, []], accessId), //Pass empty array as previous history ids
    FEE.CANCEL_ORDER * BigInt(orderIds.length) + (accessId ? HISTORY_ACCESS_FEE : 0n) + 1000000n);
};
/**
 * Creates a UI command (QR code string) for creating a single BTC-like order.
 * This function is typically used when a user wants to create one order via QR code scanning.
 * The returned string can be encoded as a QR code for wallet scanning.
 *
 * @param contractId - The L2 contract address for the BTC-like token
 * @param baseAmount - Amount of base tokens (CWEB) to spend (required)
 * @param l1Amount - Amount of L1 tokens (BTC/LTC/DOGE) to receive
 * @param minL1Amount - Minimum acceptable L1 token amount
 * @param l1Address - Recipient address on the L1 chain
 * @param chainData - BTC-like chain-specific transaction data (PSBT, l1TxId, vout)
 * @param accessId - Optional unique identifier for history tracking
 * @returns A JSON string representing the UI command for QR code generation
 * @throws {Error} If baseAmount is not defined
 *
 * @example
 * ```typescript
 * // In a React component for creating a BTC order
 * const qrCommand = createOrderBtcUiCommand({
 *   contractId: '0x...',
 *   baseAmount: 1000000000000000000n, // 1 CWEB
 *   l1Amount: 50000000n, // 0.5 BTC (in satoshis)
 *   minL1Amount: 450n,
 *   l1Address: 'bc1q...',
 *   chainData: { psbt: '...', l1TxId: '...', vout: 0 },
 *   accessId: 'tracking-id-123'
 * });
 *
 * // Use in QRCode component
 * <QRCode value={qrCommand} />
 * ```
 */
export const createOrderBtcUiCommand = ({ contractId, baseAmount, l1Amount, minL1Amount, l1Address, chainData, accessId, maxUses, }) => {
    if (!baseAmount) {
        throw new Error('createOrderBtcUiCommand: baseAmount is not defined');
    }
    return constructUiCommand([
        constructCreateOrdersCall({
            contractId,
            orders: [
                [
                    1,
                    {
                        baseAmount,
                        l1Amount,
                        minL1Amount,
                        l1Address,
                        chainData,
                        maxUses,
                    },
                ],
            ],
            accessId,
        }),
    ]);
};
/**
 * Creates a UI command (QR code string) for creating a single EVM order.
 * This function is typically used when a user wants to create one order via QR code scanning.
 * The returned string can be encoded as a QR code for wallet scanning.
 *
 * @param contractId - The L2 contract address for the EVM token
 * @param baseAmount - Amount of base tokens (CWEB) to spend (required)
 * @param l1Amount - Amount of L1 tokens (ETH/USDC/etc.) to receive
 * @param minL1Amount - Minimum acceptable L1 token amount
 * @param l1Address - Recipient address on the EVM chain (normalized)
 * @param accessId - Optional unique identifier for history tracking
 * @returns A JSON string representing the UI command for QR code generation
 * @throws {Error} If baseAmount is not defined
 *
 * @example
 * ```typescript
 * // In a React component for creating an EVM order
 * const qrCommand = createOrderEvmUiCommand({
 *   contractId: '0x...',
 *   baseAmount: 200000000000000000n, // 2 CWEB
 *   l1Amount: 100000000000000000n, // 0.1 ETH
 *   minL1Amount: 5000000000000000n, // 0.005 ETH
 *   l1Address: '0x742d35Cc6634C0532925a3b844Bc9e7595f0bEb',
 *   accessId: 'tracking-id-123'
 * });
 *
 * // Use in QRCode component
 * <QRCode value={qrCommand} />
 * ```
 */
export const createOrderEvmUiCommand = ({ contractId, baseAmount, l1Amount, minL1Amount, l1Address, accessId, maxUses, }) => {
    if (!baseAmount) {
        throw new Error('createOrderEvmUiCommand: baseAmount is not defined');
    }
    return constructUiCommand([
        constructCreateOrdersCall({
            contractId,
            orders: [
                [
                    1,
                    {
                        baseAmount,
                        l1Amount,
                        minL1Amount,
                        l1Address,
                        maxUses,
                    },
                ],
            ],
            accessId,
        }),
    ]);
};
/**
 * Creates a UI command (QR code string) for creating multiple BTC-like orders in a single batch.
 * This is more efficient than creating orders individually and is useful for market makers
 * or users creating multiple orders at once.
 *
 * @param data - Order creation data for multiple BTC orders
 * @param data.contractId - The L2 contract address for the BTC-like token
 * @param data.orders - Batch of BTC orders with chainData (PSBT, l1TxId, vout)
 * @param data.accessId - Optional unique identifier for history tracking
 * @returns A JSON string representing the UI command for QR code generation
 *
 * @example
 * ```typescript
 * // Create a batch of 3 BTC orders
 * const qrCommand = createOrdersBtcUiCommand({
 *   contractId: 'kadena:mainnet01:btc-contract...',
 *   orders: [[
 *     3, // Create 3 orders with same base template
 *     {
 *       baseAmount: 1000n,
 *       l1Amount: 500n,
 *       minL1Amount: 450n,
 *       l1Address: 'bc1q...',
 *       chainData: { psbt: '...', l1TxId: '...', vout: 0 }
 *     },
 *     [ // Optional: individual order variations (tunes)
 *       { l1Amount: 600n }, // First order variation
 *       { l1Amount: 550n }, // Second order variation
 *       { l1Amount: 500n }  // Third order variation
 *     ]
 *   ]],
 * });
 *
 * // Use in QRCode component
 * <QRCode value={qrCommand} />
 * ```
 */
export const createOrdersBtcUiCommand = (data) => {
    return constructUiCommand([constructCreateOrdersCall(data)]);
};
/**
 * Creates a UI command (QR code string) for creating multiple EVM orders in a single batch.
 * This is more efficient than creating orders individually and is useful for market makers
 * or users creating multiple orders at once.
 *
 * @param data - Order creation data for multiple EVM orders
 * @param data.contractId - The L2 contract address for the EVM token
 * @param data.orders - Batch of EVM orders (no chainData required)
 * @param data.accessId - Optional unique identifier for history tracking
 * @returns A JSON string representing the UI command for QR code generation
 *
 * @example
 * ```typescript
 * // Create a batch of 2 EVM orders
 * const qrCommand = createOrdersEvmUiCommand({
 *   contractId: '0x...',
 *   orders: [[
 *     2, // Create 2 orders
 *     {
 *       baseAmount: 2000n,
 *       l1Amount: 1000n,
 *       minL1Amount: 950n,
 *       l1Address: '0x742d35Cc6634C0532925a3b844Bc9e7595f0bEb'
 *     },
 *     [ // Optional: individual order variations
 *       { l1Address: '0x1111...' }, // First order to different address
 *       { l1Address: '0x2222...' }  // Second order to different address
 *     ]
 *   ]],
 * });
 *
 * // Use in QRCode component
 * <QRCode value={qrCommand} />
 * ```
 */
export const createOrdersEvmUiCommand = (data) => {
    return constructUiCommand([constructCreateOrdersCall(data)]);
};
/**
 * Creates a UI command (QR code string) for canceling a single order.
 * This function is typically used when a user wants to cancel one order via QR code scanning.
 * The returned string can be encoded as a QR code for wallet scanning.
 *
 * @param contractId - The L2 contract address where the order exists
 * @param orderId - The unique identifier of the order to cancel
 * @param accessId - Optional unique identifier for history tracking
 * @returns A JSON string representing the UI command for QR code generation
 *
 * @example
 * ```typescript
 * // In a React component for canceling an order
 * const qrCommand = cancelOrderUiCommand({
 *   contractId: getContractParams(Currency.BTC).L2_CONTRACT_ADDRESS_BASE,
 *   orderId: 'order-123-abc',
 *   accessId: isTracked ? uniqueTrackingData : undefined
 * });
 *
 * // Use in QRCode component
 * <QRCode value={qrCommand} />
 * ```
 */
export const cancelOrderUiCommand = ({ contractId, orderId, accessId }) => {
    return constructUiCommand([constructCancelOrdersCall({ contractId, orderIds: [orderId], accessId })]);
};
/**
 * Creates a UI command (QR code string) for canceling multiple orders in a single batch.
 * This is more efficient than canceling orders individually and is useful when a user
 * wants to cancel multiple orders at once.
 *
 * @param data - Order cancellation data
 * @param data.contractId - The L2 contract address where orders exist
 * @param data.orderIds - Array of order IDs to cancel
 * @param data.accessId - Optional unique identifier for history tracking
 * @returns A JSON string representing the UI command for QR code generation
 *
 * @example
 * ```typescript
 * // Cancel multiple orders at once
 * const qrCommand = cancelOrdersUiCommand({
 *   contractId: '0x...',
 *   orderIds: ['order-1', 'order-2', 'order-3'],
 *   accessId: 'tracking-id-123'
 * });
 *
 * // Use in QRCode component
 * <QRCode value={qrCommand} />
 * ```
 */
export const cancelOrdersUiCommand = (data) => {
    return constructUiCommand([constructCancelOrdersCall(data)]);
};
